/**
 * Kopf der Sektions-Detailseite (P1-V3 § 2.8, Umbau 25.10.2026) — nur hinter `quelle.sektion`, die alte Seite
 * /webinar-auswertungen/:id bleibt unverändert. Persona-Umbau B9 (28.10.2026, Plan 2026-10-28-persona-umbau-paket-1-2):
 * - „Der Termin": Anmeldungen, Show-up, Anfos, Erwarteter Wert und Erwarteter Wert je 1.000 Kontakte (Einladung), jede
 *   Kachel mit „typisch" und ▲/▼ — „typisch" = Median der fertigen Termine der 12 Monate VOR diesem Termin
 *   (`fetchTypischVorTermin`, Plan 2026-10-30-vergleichsfenster-drift; bis dahin die 12 Monate vor heute). Je Größe erst ab
 *   5 Terminen, sonst kein Vergleich — kein Rückfall auf einen anderen Zeitraum. Bei reifen Terminen (≥ 75 Tage oder Hochrechnung aus eigenen Zahlungen) eine sechste Kachel
 *   „Hochrechnung / Bisher bezahlt". Die frühere Textzeile „Weitere Kennzahlen" ist in Kacheln und Unterzeilen aufgegangen.
 * - „Im Raum" (nur Termine im eigenen Raum): die Raten als Hauptwerte, Anzahlen klein; vier getrennte Größen, kein Trichter.
 *   Ohne „typisch" — die Raum-Termine haben keinen gemeinsamen Abruf (Plan: nur ohne großen Aufwand).
 * Kacheldesign `Kachel`/`KACHEL_RASTER` wie P1–P4 (statt `KpiTile`). CPA-Kacheln entfallen (Abrechnungsgröße der alten Seite).
 */
import { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';
import { BefundZeile, BezahltBuendel, euroJeTausend, Kachel, KACHEL_RASTER, kompakt, tEuro, Teile } from '../../../components/auswertungen/kennzahlTeile';
import { formatEur, formatPct, KPI, TERMS } from '../../../helpers/kpiDefinitions';
import type { DetailKennzahlen, TypischDetail } from '../../../helpers/auswertungenData';
import type { RoomRetentionData, WebinarEngagementDetailData } from '../../../helpers/webinarStatisticsData';
import { formatNumber } from '../../../helpers/numberUtils';
import InfoTip from '../../../components/InfoTip';

/** Zählregel „Anfos von Teilnehmern" (K2, festgelegt 25.10.: über die Anmeldung) — sichtbar erklärt (B55). */
const TEILNEHMER_ANFOS_TIP =
  'Anfos, deren Anmeldung (über die Kauf-RID bzw. die Anmelde-E-Mail) einer Person gehört, die live im Webinar war; ohne vorläufig ergänzte. Andere Zählungen ergeben andere Zahlen: nur über die Bestell-E-Mail meist etwas weniger, „Käufer unter den Klickern“ zählt Personen, die auf das Angebot geklickt haben — die Größen bilden keinen Trichter.';

import { GegenTypisch, KURZ_GRUND, SHOWUP_GRUND } from '../webinare-produkte/shared';
import { abstandTypisch } from '../webinare-produkte/befunde';
import { anfoUeberleitung } from './anfoUeberleitung';
import { RaumQuote, raumBewertung } from './raumMessung';
import { detailBefund, detailBefundEingabe } from './detailBefund';

const n = (v: number | null | undefined) => (v === null || v === undefined ? '–' : v.toLocaleString('de-DE'));

/** Wert einer Raum-Quote: Prozent oder „—" mit dem Grund als Tooltip. */
const raumWert = (q: RaumQuote, format: (w: number) => string): React.ReactNode =>
  q.wert === null ? (
    <Box component="span" title={q.grund ?? undefined} sx={{ cursor: 'help' }}>
      —
    </Box>
  ) : (
    format(q.wert)
  );

/** Heute (Berlin) als YYYY-MM-DD — Ende des Zeitraums im Link auf die Bestellungen. */
const heuteBerlin = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });

/** Zeile unter der Anfo-Kachel: „+ 11 verweigert (53 Bestellungen · 21 %)", verlinkt auf die Bestellungen. */
const VerweigertZeile: FC<{ d: DetailKennzahlen }> = ({ d }) => {
  const v = d.verweigert;
  if (!v || !(v.verweigert > 0) || !(v.bestellungen > 0)) return null;
  // Suche nach der Webinar-Kennung, nicht nach dem Titel: Wiederholungen tragen denselben Titel (KCV8vu: 49 Bestellungen,
  // die Titelsuche fand 125). `liste=verweigert` wählt den Schnellfilter der Liste vor.
  const ziel = `/auswertungen/bestellungen?startDate=${d.start_date.slice(0, 10)}&endDate=${heuteBerlin()}&suche=${encodeURIComponent(d.webinar_id)}&liste=verweigert`;
  return (
    <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
      <Link component={RouterLink} to={ziel} underline="hover" color="inherit" sx={{ textDecorationStyle: 'dotted' }}>
        <Teile
          sx={{ display: 'inline' }}
          teile={[`+ ${n(v.verweigert)} ${TERMS.verweigert.short}`, `${formatPct((v.verweigert * 100) / v.bestellungen, 0)} von ${n(v.bestellungen)} ${TERMS.bestellungenAlle.short}`]}
        />
      </Link>
      <InfoTip text={`${TERMS.bestellungenAlle.label}: ${TERMS.bestellungenAlle.tip} ${gross(TERMS.verweigert.short)}: ${TERMS.verweigert.tip} Der Link öffnet die Bestellliste ab dem Webinartag, gefiltert auf dieses Webinar und die verweigerten.`} />
    </Box>
  );
};
const gross = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** Ab diesem Alter (Tage) gilt ein Termin als reif für „Hochrechnung / Bisher bezahlt" (Plan B9). */
const REIF_TAGE = 75;
/** Alter in ganzen Tagen ab dem Kalendertag des Termins (Berlin), ohne Uhrzeit. */
const alterTage = (start: string): number => {
  const heute = heuteBerlin();
  return Math.round((Date.parse(`${heute}T00:00:00Z`) - Date.parse(`${start.slice(0, 10)}T00:00:00Z`)) / 86_400_000);
};
const zeile = (inhalt: React.ReactNode, sx: object = {}) => (
  <Box component="span" sx={{ display: 'block', mt: 0.25, ...sx }}>
    {inhalt}
  </Box>
);

const Reihe: FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
      {titel}
    </Typography>
    <Box sx={{ ...KACHEL_RASTER, mb: 2 }}>{children}</Box>
  </Box>
);

const SektionKopf: FC<{ e: WebinarEngagementDetailData; d: DetailKennzahlen | null; raum: RoomRetentionData | null; typisch?: TypischDetail | null }> = ({
  e,
  d,
  raum,
  typisch = null,
}) => {
  const rechtzeitigDabei = (e as WebinarEngagementDetailData & { attended_timely?: number }).attended_timely ?? null;
  const eingestiegen = Math.max(0, e.registrations - e.timely);
  const zuSpaet = e.registrations_after_start ?? 0;
  const room = raum?.room ?? null;
  const gesehen = e.cta_views;
  const anfosTeiln = e.attendee_orders;
  // P5-Review #3: Teilnahme nicht messbar, wenn die Show-up-Kachel „—" zeigt (Messlücke, übernommen, Tracking-Warnung)
  // — dann keine Aufteilung nach „live dabei" und keine Quote je 100 Teilnehmer. Bis `d` da ist, ist das offen.
  const teilnahmeUnmessbar = e.tracking_suspect || !!d?.show_up?.grund;
  const raumB = room
    ? raumBewertung({
        zuschauer: room.viewers,
        teilnehmer: teilnahmeUnmessbar ? null : e.attendees,
        gesehen,
        klicks: e.cta_clicks,
        kaeufer: e.cta_buyers ?? null,
      })
    : null;
  // Überleitung statt nur „N von Teilnehmern": die Teile summieren exakt zur Kachelzahl (GF-Rückfrage 27.10.).
  const ueberleitung = anfoUeberleitung({ ...e, teilnahmeUnmessbar });
  // P5-Review #26: jeder Teil bleibt zusammen, umbrochen wird nur an „·" (sonst „nicht / live", „1 ohne / Anmeldung").
  const anfoTeile = ueberleitung
    ? ueberleitung.map((t) => `${n(t.wert)} ${t.label}`)
    : [
        teilnahmeUnmessbar ? 'Teilnahme nicht messbar' : `${n(anfosTeiln)} von Teilnehmern`,
        ...((e.orders_provisional ?? 0) > 0 ? [`${n(e.orders_provisional)} vorläufig`] : []),
      ];
  // „typisch" = Median der 12 Monate vor dem Termin; fehlt er (unter 5 Terminen, Abruf gescheitert), kein Vergleich.
  const t = typisch;
  const typischWert = t?.wert_summe_fest ?? null;
  const tipT = TERMS.gegenTypischVorTermin.tip;
  const offen = !!d?.zahlen_offen_bis;
  const fest = e.orders - (e.orders_provisional ?? 0);
  const je100Anm = e.registrations > 0 ? formatNumber((fest * 100) / e.registrations, 1) : null;
  const je100Teiln = !teilnahmeUnmessbar && d && e.attendees > 0 ? formatNumber((anfosTeiln * 100) / e.attendees, 1) : null;
  const reif = !!d && (alterTage(d.start_date) >= REIF_TAGE || (d.anfos_aus_zahlung ?? 0) > 0);
  const bezahlt =
    d && d.paid_revenue !== undefined ? <BezahltBuendel inline wert={tEuro(d.paid_revenue)} zahler={d.payers ?? 0} alterTage={d.avg_age_days ?? null} /> : null;
  const anfoSub = !d ? (
    '…'
  ) : (
    <>
      {t && <GegenTypisch abstand={abstandTypisch(e.orders, t.anfos)} typischText={n(t.anfos === null ? null : Math.round(t.anfos))} offen={offen} tip={tipT} />}
      {/* Quoten aus der früheren Zeile „Weitere Kennzahlen" — ohne vorläufige im Zähler (Regel 13). */}
      <Teile
        sx={{ mt: 0.5 }}
        teile={[
          ...(je100Anm ? [`${je100Anm} ${TERMS.anfosJe100Anm.label}`] : []),
          ...(je100Teiln ? [`${je100Teiln} ${TERMS.anfosJeTeilnehmer.je100Label}`] : []),
        ]}
      />
      <Teile sx={{ mt: 0.25 }} teile={anfoTeile} />
      <VerweigertZeile d={d} />
    </>
  );
  const showUp = d?.show_up ?? null;
  // C5 (Audit 30.10.): ein Satz aus denselben Vergleichen wie die ▲/▼ der Kacheln; erst mit den Kennzahlen (`d`).
  const befund = d ? detailBefund(detailBefundEingabe(e, d), t) : null;
  return (
    <Box sx={{ mb: 3 }}>
      {befund && <BefundZeile text={befund} tip={tipT} />}
      <Reihe titel="Der Termin">
        <Kachel
          wert={n(e.registrations)}
          label={TERMS.anmeldungenLive.label}
          tip={`${TERMS.anmeldungenLive.tip} Davon ${n(eingestiegen)} während des Webinars eingestiegen; der Show-up rechnet mit den ${n(e.timely)} vor Beginn Angemeldeten.`}
          sub={
            <>
              {t && <GegenTypisch abstand={abstandTypisch(e.registrations, t.registrations)} typischText={n(t.registrations === null ? null : Math.round(t.registrations))} tip={tipT} />}
              {zuSpaet > 0 && zeile(`+${n(zuSpaet)} zu spät für live, nicht mitgezählt`)}
            </>
          }
        />
        {/* Show-up aus derselben Entscheidung wie die Kapitelseite (`d.show_up`, Backend `showUpZeile`): Messlücke,
            Tracking-Warnung und übernommene Anmeldungen zeigen „—" mit Grund statt der Rohquote. */}
        <Kachel
          wert={!showUp ? '…' : showUp.show_up === null ? '—' : formatPct(showUp.show_up)}
          label={`${TERMS.showUpTermin.label} ${TERMS.showUpTermin.einheit}`}
          tip={showUp?.grund ? `${TERMS.showUpTermin.tip} ${SHOWUP_GRUND[showUp.grund]}` : TERMS.showUpTermin.tip}
          sub={
            !showUp ? undefined : showUp.grund ? (
              KURZ_GRUND[showUp.grund]
            ) : (
              <>
                {t && t.show_up !== null && (
                  <GegenTypisch
                    abstand={abstandTypisch(showUp.show_up, t.show_up, 'pp')}
                    typischText={formatPct(t.show_up, 0)}
                    tip={`${tipT}${t.show_up_termine !== null ? ` Typischer Show-up aus ${n(t.show_up_termine)} Terminen mit belastbarer Teilnahme.` : ''}`}
                  />
                )}
                {zeile(`${n(showUp.attended_timely ?? rechtzeitigDabei)} von ${n(showUp.timely ?? e.timely)} vor Beginn Angemeldeten`)}
              </>
            )
          }
        />
        <Kachel
          wert={n(e.orders)}
          label={TERMS.anfos.label}
          tip={`${TERMS.anfos.tip} Die Zeile darunter zerlegt die Zahl ohne Rest. „Von Teilnehmern“: ${TEILNEHMER_ANFOS_TIP} „Angemeldet, nicht live“: angemeldet, aber nicht live dabei (z. B. über die Aufzeichnung gekauft). „Ohne Anmeldung“: Kauf, zu dem keine Anmeldung gehört. „Vorläufig“: ${TERMS.vorlaeufig.tip}`}
          sub={anfoSub}
        />
        <Kachel
          wert={d ? tEuro(d.wert_summe_fest) : '…'}
          label={TERMS.wertSumme.label}
          tip={`${TERMS.wertSumme.tip} Dieselbe Zahl wie in der Webinar-Tabelle des Kapitels.`}
          sub={
            d ? (
              <>
                {typischWert !== null && <GegenTypisch abstand={abstandTypisch(d.wert_summe_fest, typischWert)} typischText={tEuro(typischWert)} offen={offen} tip={tipT} />}
                <Teile
                  sx={{ mt: 0.5 }}
                  teile={[
                    `Ø ${formatEur(d.with_value_fest > 0 ? d.wert_summe_fest / d.with_value_fest : null, 0)} je Anfo`,
                    ...(d.zahlen_offen_bis ? [`${TERMS.zahlenOffen.short} ${d.zahlen_offen_bis.slice(8, 10)}.${d.zahlen_offen_bis.slice(5, 7)}.`] : []),
                    ...(d.wert_geschaetzt_anfos > 0 ? [TERMS.wertGeschaetzt.short] : []),
                  ]}
                />
                {/* Junger Termin: „Bisher bezahlt" klein hier (die eigene Kachel kommt erst, wenn er reif ist). */}
                {!reif && bezahlt && (d.paid_revenue ?? 0) > 0 && zeile(<>{TERMS.umsatzNetto.label} {bezahlt}</>)}
              </>
            ) : undefined
          }
        />
        <Kachel
          wert={!d ? '…' : d.wert_je_1k_fest === null ? '—' : euroJeTausend(d.wert_je_1k_fest)}
          label={`${TERMS.wertJe1kEinladung.label} (Einladung)`}
          tip={TERMS.wertJe1kEinladung.tip}
          sub={
            d ? (
              <>
                {d.wert_je_1k_fest !== null && t && t.value_per_1k !== null && (
                  <GegenTypisch abstand={abstandTypisch(d.wert_je_1k_fest, t.value_per_1k)} typischText={euroJeTausend(t.value_per_1k)} tip={tipT} />
                )}
                {d.wert_je_1k_fest === null
                  ? zeile(
                      d.contacts > 0
                        ? 'noch nicht gerechnet: die Einladungen sind jünger als 14 Tage oder liegen nicht alle im Kontaktfenster'
                        : 'keine eigenen Einladungen gezählt (Kontakte erst ab 23.12.2025)',
                    )
                  : zeile(`aus ${n(d.send_anfos)} Anfos über die Einladung · ${kompakt(d.contacts)} Kontakte`, { mt: 0.5 })}
                {d.unsubscribes_per_1k !== null && zeile(`${TERMS.abmeldungenJe1kVersand.label} ${formatNumber(d.unsubscribes_per_1k, 1)}`)}
              </>
            ) : undefined
          }
        />
        {reif && d && (
          <Kachel
            wert={tEuro(e.ltv_revenue)}
            label={`${TERMS.hochrechnung.short} / ${TERMS.umsatzNetto.label}`}
            tip={`${TERMS.hochrechnung.label}: ${TERMS.hochrechnung.tip} ${TERMS.umsatzNetto.label}: ${TERMS.umsatzNetto.tip}`}
            sub={
              <>
                {d.wert_summe_fest > 0 && zeile(`${formatPct((e.ltv_revenue * 100) / d.wert_summe_fest, 0)} des Erwarteten Werts`)}
                {d.anfos_aus_zahlung !== undefined &&
                  zeile(
                    d.anfos_aus_zahlung === 0
                      ? 'noch keine eigene Aussage, gleich dem Erwarteten Wert'
                      : `aus eigenen Zahlungen: ${n(d.anfos_aus_zahlung)} von ${n(d.anfos - d.provisional)} Anfos`,
                  )}
                {bezahlt && zeile(<>{TERMS.umsatzNetto.label} {bezahlt}</>, { mt: 0.5, color: 'text.primary' })}
              </>
            }
          />
        )}
      </Reihe>
      {room && raumB && (
        <Reihe titel="Im Raum — vier getrennte Größen, kein Trichter">
          {/* B1 (Audit 30.10.): Quoten nur mit gültigem Nenner aus derselben Messung — nie über 100 %, nie gekappt
              (`raumMessung.ts`). Bei unvollständiger Raum-Messung nur Anzahlen, die Anteile stehen als „—" mit Grund. */}
          <Kachel
            wert={n(room.viewers)}
            label={TERMS.teilnehmerImRaum.label}
            tip={TERMS.teilnehmerImRaum.tip}
            sub={
              raumB.unvollstaendig ? (
                <Box component="span" sx={{ color: 'warning.dark' }}>
                  {raumB.unvollstaendig}
                </Box>
              ) : eingestiegen > 0 ? (
                `davon ${n(eingestiegen)} während des Webinars eingestiegen`
              ) : undefined
            }
          />
          <Kachel
            wert={raumWert(raumB.gesehen, (w) => `mind. ${formatPct(w, 0)}`)}
            label={`${KPI.cta_views.label}, Anteil der Teilnehmer`}
            tip={`${KPI.cta_views.tooltip} „Mindestens“, weil die Einblendung nicht bei jedem Zuschauer gemessen wird.`}
            sub={raumB.gesehen.wert === null ? `mindestens ${n(gesehen)} Personen · ${raumB.gesehen.grund}` : `mindestens ${n(gesehen)} von ${n(room.viewers)}`}
          />
          <Kachel
            wert={raumWert(raumB.geklickt, (w) => formatPct(w, 0))}
            label={`${KPI.cta_clicks.label}, Anteil derer, die es sahen`}
            tip={`${KPI.cta_ctr.tooltip} Der Anteil ist eher zu hoch, weil „gesehen“ eine Untergrenze ist.`}
            sub={raumB.geklickt.wert === null ? `${n(e.cta_clicks)} Personen · ${raumB.geklickt.grund}` : `${n(e.cta_clicks)} von mind. ${n(gesehen)}`}
          />
          {e.cta_buy_rate != null && (
            <Kachel
              wert={raumWert(raumB.kauf, (w) => formatPct(w, 0))}
              label={KPI.cta_buy_rate.label}
              tip={KPI.cta_buy_rate.tooltip}
              sub={raumB.kauf.wert === null ? `${n(e.cta_buyers ?? 0)} Käufer · ${raumB.kauf.grund}` : `${n(e.cta_buyers ?? 0)} von ${n(e.cta_clicks)}`}
            />
          )}
        </Reihe>
      )}
    </Box>
  );
};

export default SektionKopf;
